import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { useAuth } from '@restworld/feature-authentication';
import { Heading } from '@restworld/ui-ds-base';
import type { Note as TNote } from '@restworld/utility-types';
import { cn, formatDate } from '@restworld/utils-common';

import { CandidateNotesEditDeleteAction } from './candidate-notes-edit-delete-action';

const NoteContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('border-t border-neutral-40 py-3 w-full', className)} {...props} />
);

const NoteHeader = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('text-neutral-700 flex justify-between items-center mb-3', className)} {...props} />
);

const UserInfo = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex items-center gap-3', className)} {...props} />
);

const UserAvatar = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'h-8 w-8 rounded-full border border-purple-700 font-semibold text-purple-700 grid place-items-center bg-purple-50',
      className
    )}
    {...props}
  />
);

const UserName = ({ className, ...props }: any) => <Heading className={cn('text-neutral-700', className)} {...props} />;

const NoteDate = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('text-neutral-100 font-medium', className)} {...props} />
);

// The note body is comment text, not a heading: pin it to body size + normal weight. A bare <Heading>
// defaults to size 4 (text-xl) + semibold, which rendered comments bigger and bolder than the author name.
const NoteContent = ({ className, ...props }: any) => (
  <Heading size={5} weight="normal" className={cn('w-fit truncate', className)} {...props} />
);

export const CandidateNoteItem = ({
  last_modified_by_user: lastModifiedByUser,
  inserted_at: insertedAt,
  note,
  id,
}: TNote) => {
  const { t } = useTranslation('job-positions-page');
  const name = lastModifiedByUser?.name ?? '';
  const surname = lastModifiedByUser?.surname ?? '';
  const userId = lastModifiedByUser?.id;
  const { user } = useAuth();
  const title = useMemo(
    () => (userId === user?.id ? t('you') : `${name ?? '-'} ${surname ?? ''}`),
    [userId, user?.id, t, name, surname]
  );
  return (
    <NoteContainer id={id?.toString()}>
      <NoteHeader>
        <UserInfo>
          <UserAvatar>
            <span>
              {name?.split('')[0] ?? 'N'}
              {surname?.split('')[0] ?? 'A'}
            </span>
          </UserAvatar>
          <div>
            <UserName size={5}>{title}</UserName>
            <NoteDate>{formatDate(insertedAt)}</NoteDate>
          </div>
        </UserInfo>
        {userId === user?.id && <CandidateNotesEditDeleteAction noteId={id} note={note} />}
      </NoteHeader>
      <NoteContent color="neutral">{note}</NoteContent>
    </NoteContainer>
  );
};
